import React from 'react';
import { useTranslation } from 'react-i18next';
import CRMAttachmentPicker from './CRMAttachmentPicker';
import SaveAttachmentToCRM from './SaveAttachmentToCRM';
import EmailLinkManager from './EmailLinkManager';
import MailComposeModal from './MailComposeModal';
import MailContactPickerModal from './MailContactPickerModal';
import MailCourtierClientsModal from './MailCourtierClientsModal';
import MailCompanyContactsModal from './MailCompanyContactsModal';
import MailLinkModal from './MailLinkModal';
import MailCreateFolderModal from './MailCreateFolderModal';
import MailRenameFolderModal from './MailRenameFolderModal';
import MailUnknownContactModal from './MailUnknownContactModal';
import MinimizedMailTabs from './MinimizedMailTabs';
import MailAttachmentPreviewModal from './MailAttachmentPreviewModal';

const MailModals = ({ commonProps, composeProps, contactsProps, folderProps, attachmentProps }) => {
  const { t } = useTranslation('admin');
  // Props communes (infrastructure partagée par plusieurs modales)
  const {
    EMOJI_LIST, authToken, backendUrl, emailAccounts, selectedAccount, setSelectedAccount, showSuccess, loadEmails
  } = commonProps;

  // Modale de composition d'email (éditeur, pièces jointes, émojis, tableaux, drag & drop)
  const {
    attachments, closeComposeModal, composeForm, composeModalDragging, composeModalPosition,
    composeModalRef, handleDragEnter, handleDragLeave, handleDragOver, handleDrop, handleFileSelect,
    handleImageInsert, imageInputRef, insertEmoji, insertTable, isDragging, openComposeInNewWindow,
    openContactPicker, quillFormats, quillModules, quillRef, removeAttachment, replyMode,
    sendEmail, setComposeForm, setComposeModalDragOffset, setComposeModalDragging,
    setShowEmojiPicker, setShowTableDialog, setTableCols, setTableRows, showComposeModal, showEmojiPicker,
    showTableDialog, tableCols, tableRows,
    minimizedWindows, minimizeCurrentWindow, restoreMinimizedWindow, closeMinimizedWindow, updateMinimizedTabPosition
  } = composeProps;

  // Modales contacts/liaisons (annuaire, clients courtier, contacts compagnie, contact inconnu, liaison)
  const {
    activeContactTab, addToSelectedLinks, companiesList, companyContacts, contactPickerField, contactResults,
    contactSearchQuery, courtierClients, createQuickAnnuaireContact, createQuickClient, createQuickCourtier, creatingContact,
    currentPage, deselectAllCompanyContacts, deselectAllCourtierClients, deselectAllFromTab, getSelectedCountByCategory, getUniqueCompanies,
    isEmailSelected, linkSearchQuery, linkSearchResults, linkSuggestions, openCompanyContacts, openCourtierClients,
    paginateItems, quickAnnuaireForm, quickClientForm, quickCourtierForm, removeFromSelectedLinks, renderPagination,
    saveLinkAndProcess, searchContacts, searchEntitiesForLink, selectAllCompanyContacts, selectAllCourtierClients, selectAllFromTab,
    selectContactEmail, selectedCompany, selectedCourtier, selectedLinks, setActiveContactTab, setContactSearchQuery,
    setLinkSearchQuery, setQuickAnnuaireForm, setQuickClientForm, setQuickCourtierForm, setShowCompanyContacts, setShowContactPicker,
    setShowCourtierClients, setShowLinkModal, setShowUnknownContactModal, setUnknownContactStep, setUnknownContactType, showCompanyContacts,
    showContactPicker, showCourtierClients, showLinkModal, showUnknownContactModal, unknownContactEmail, unknownContactStep,
    unknownContactType
  } = contactsProps;

  // Modales dossiers personnalisés (création / renommage)
  const {
    createFolder, folderToRename, newFolderName, renameFolder, renameFolderName, setFolderToRename,
    setNewFolderName, setRenameFolderName, setShowCreateFolderModal, setShowRenameFolderModal, showCreateFolderModal, showRenameFolderModal
  } = folderProps;

  // Modales pièces jointes / prévisualisation / gestionnaire de liaisons multiples
  const {
    attachmentToSave, closePreviewModal, emailToLink, handleCRMAttachments, previewModal, renderPreviewContent,
    setAttachmentToSave, setEmailToLink, setPreviewModal, setSelectedEmail, setShowCRMAttachmentPicker, setShowEmailLinkManager, setShowSaveAttachmentModal,
    showCRMAttachmentPicker, showEmailLinkManager, showSaveAttachmentModal
  } = attachmentProps;

  return (
    <>
      {/* Compose Modal */}
      {showComposeModal && (
        <MailComposeModal
          EMOJI_LIST={EMOJI_LIST}
          attachments={attachments}
          closeComposeModal={closeComposeModal}
          composeForm={composeForm}
          composeModalDragging={composeModalDragging}
          composeModalPosition={composeModalPosition}
          composeModalRef={composeModalRef}
          emailAccounts={emailAccounts}
          handleDragEnter={handleDragEnter}
          handleDragLeave={handleDragLeave}
          handleDragOver={handleDragOver}
          handleDrop={handleDrop}
          handleFileSelect={handleFileSelect}
          handleImageInsert={handleImageInsert}
          imageInputRef={imageInputRef}
          insertEmoji={insertEmoji}
          insertTable={insertTable}
          isDragging={isDragging}
          minimizeCurrentWindow={minimizeCurrentWindow}
          openComposeInNewWindow={openComposeInNewWindow}
          openContactPicker={openContactPicker}
          quillFormats={quillFormats}
          quillModules={quillModules}
          quillRef={quillRef}
          removeAttachment={removeAttachment}
          replyMode={replyMode}
          selectedAccount={selectedAccount}
          sendEmail={sendEmail}
          setComposeForm={setComposeForm}
          setComposeModalDragOffset={setComposeModalDragOffset}
          setComposeModalDragging={setComposeModalDragging}
          setPreviewModal={setPreviewModal}
          setSelectedAccount={setSelectedAccount}
          setShowCRMAttachmentPicker={setShowCRMAttachmentPicker}
          setShowEmojiPicker={setShowEmojiPicker}
          setShowTableDialog={setShowTableDialog}
          setTableCols={setTableCols}
          setTableRows={setTableRows}
          showEmojiPicker={showEmojiPicker}
          showTableDialog={showTableDialog}
          tableCols={tableCols}
          tableRows={tableRows}
        />
      )}

      {/* Onglets des fenêtres minimisées — gérés globalement dans AdminDashboard */}


      {/* Modal Annuaire de Contacts */}
      {showContactPicker && (
        <MailContactPickerModal
          activeContactTab={activeContactTab}
          composeForm={composeForm}
          contactPickerField={contactPickerField}
          contactResults={contactResults}
          contactSearchQuery={contactSearchQuery}
          currentPage={currentPage}
          deselectAllFromTab={deselectAllFromTab}
          getSelectedCountByCategory={getSelectedCountByCategory}
          getUniqueCompanies={getUniqueCompanies}
          isEmailSelected={isEmailSelected}
          openCompanyContacts={openCompanyContacts}
          openCourtierClients={openCourtierClients}
          paginateItems={paginateItems}
          renderPagination={renderPagination}
          searchContacts={searchContacts}
          selectAllFromTab={selectAllFromTab}
          selectContactEmail={selectContactEmail}
          setActiveContactTab={setActiveContactTab}
          setContactSearchQuery={setContactSearchQuery}
          setShowContactPicker={setShowContactPicker}
        />
      )}


      {/* Modal Clients du Courtier */}
      {showCourtierClients && selectedCourtier && (
        <MailCourtierClientsModal
          courtierClients={courtierClients}
          currentPage={currentPage}
          deselectAllCourtierClients={deselectAllCourtierClients}
          isEmailSelected={isEmailSelected}
          paginateItems={paginateItems}
          renderPagination={renderPagination}
          selectAllCourtierClients={selectAllCourtierClients}
          selectContactEmail={selectContactEmail}
          selectedCourtier={selectedCourtier}
          setShowCourtierClients={setShowCourtierClients}
        />
      )}





      {/* Modal Contacts de la Compagnie */}
      {showCompanyContacts && selectedCompany && (
        <MailCompanyContactsModal
          companyContacts={companyContacts}
          currentPage={currentPage}
          deselectAllCompanyContacts={deselectAllCompanyContacts}
          isEmailSelected={isEmailSelected}
          paginateItems={paginateItems}
          renderPagination={renderPagination}
          selectAllCompanyContacts={selectAllCompanyContacts}
          selectContactEmail={selectContactEmail}
          selectedCompany={selectedCompany}
          setShowCompanyContacts={setShowCompanyContacts}
        />
      )}

      {/* Modal de prévisualisation (composant partagé) */}
      <MailAttachmentPreviewModal
        previewModal={previewModal}
        onClose={closePreviewModal}
        renderContent={renderPreviewContent}
        showSuccess={showSuccess}
        onSaveToCrm={(pm) => {
          setAttachmentToSave({
            attachment: { original_filename: pm.filename },
            emailId: pm.emailId,
            attachmentIndex: pm.attachmentIndex
          });
          setShowSaveAttachmentModal(true);
        }}
      />

      {/* Modal sélecteur de pièces jointes CRM */}
      <CRMAttachmentPicker
        isOpen={showCRMAttachmentPicker}
        onClose={() => setShowCRMAttachmentPicker(false)}
        onSelectAttachments={handleCRMAttachments}
        backendUrl={backendUrl}
        authToken={authToken}
      />

      {/* Modal enregistrer pièce jointe dans CRM */}
      <SaveAttachmentToCRM
        isOpen={showSaveAttachmentModal}
        onClose={() => {
          setShowSaveAttachmentModal(false);
          setAttachmentToSave(null);
        }}
        attachment={attachmentToSave?.attachment}
        emailId={attachmentToSave?.emailId}
        attachmentIndex={attachmentToSave?.attachmentIndex}
        backendUrl={backendUrl}
        authToken={authToken}
        onSuccess={(message) => showSuccess(message)}
      />

      {/* Modal de liaison CRM */}
      {showLinkModal && (
        <MailLinkModal
          addToSelectedLinks={addToSelectedLinks}
          linkSearchQuery={linkSearchQuery}
          linkSearchResults={linkSearchResults}
          linkSuggestions={linkSuggestions}
          removeFromSelectedLinks={removeFromSelectedLinks}
          saveLinkAndProcess={saveLinkAndProcess}
          searchEntitiesForLink={searchEntitiesForLink}
          selectedLinks={selectedLinks}
          setLinkSearchQuery={setLinkSearchQuery}
          setShowLinkModal={setShowLinkModal}
        />
      )}

      {/* Modal Création Dossier */}
      {showCreateFolderModal && (
        <MailCreateFolderModal
          createFolder={createFolder}
          newFolderName={newFolderName}
          setNewFolderName={setNewFolderName}
          setShowCreateFolderModal={setShowCreateFolderModal}
        />
      )}

      {/* Modal Renommer Dossier */}
      {showRenameFolderModal && folderToRename && (
        <MailRenameFolderModal
          renameFolder={renameFolder}
          renameFolderName={renameFolderName}
          setFolderToRename={setFolderToRename}
          setRenameFolderName={setRenameFolderName}
          setShowRenameFolderModal={setShowRenameFolderModal}
        />
      )}

      {/* Modal Création Contact Inconnu */}
      {showUnknownContactModal && (
        <MailUnknownContactModal
          companiesList={companiesList}
          createQuickAnnuaireContact={createQuickAnnuaireContact}
          createQuickClient={createQuickClient}
          createQuickCourtier={createQuickCourtier}
          creatingContact={creatingContact}
          quickAnnuaireForm={quickAnnuaireForm}
          quickClientForm={quickClientForm}
          quickCourtierForm={quickCourtierForm}
          setQuickAnnuaireForm={setQuickAnnuaireForm}
          setQuickClientForm={setQuickClientForm}
          setQuickCourtierForm={setQuickCourtierForm}
          setShowUnknownContactModal={setShowUnknownContactModal}
          setUnknownContactStep={setUnknownContactStep}
          setUnknownContactType={setUnknownContactType}
          unknownContactEmail={unknownContactEmail}
          unknownContactStep={unknownContactStep}
          unknownContactType={unknownContactType}
        />
      )}

      {/* Modal de gestion des liaisons multiples */}
      <EmailLinkManager
        isOpen={showEmailLinkManager}
        onClose={() => {
          setShowEmailLinkManager(false);
          setEmailToLink(null);
        }}
        emailId={emailToLink?.id}
        emailSubject={emailToLink?.subject}
        backendUrl={backendUrl}
        authToken={authToken}
        onLinkAdded={() => {
          // Rafraîchir la liste des emails
          loadEmails();
          // Re-fetcher l'email sélectionné pour mettre à jour linked_client_id et afficher le bouton "Classer"
          if (emailToLink?.id && setSelectedEmail) {
            fetch(`${backendUrl}/api/admin/mail/inbox/${emailToLink.id}`, {
              headers: { 'Authorization': `Bearer ${authToken}` }
            })
              .then(r => r.ok ? r.json() : null)
              .then(data => { if (data) setSelectedEmail(data); })
              .catch(() => {});
          }
        }}
      />

    </>
  );
};

export default MailModals;
